import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import type { NotificationItem, TaskReminderItem } from "@/types/domain";

// Same-origin negotiate proxy: the server adds the hub token, so it never reaches the browser.
const SIGNALR_URL = "/api/signalr";

type Options = {
  onNotification: (n: NotificationItem) => void;
  onTask: (t: TaskReminderItem) => void;
};

export function useSignalRNotifications({ onNotification, onTask }: Options) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const onNotificationRef = useRef(onNotification);
  const onTaskRef = useRef(onTask);
  onNotificationRef.current = onNotification;
  onTaskRef.current = onTask;

  useEffect(() => {
    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    function stop() {
      stopped = true;
      clearTimeout(retryTimer);
      void connection.stop();
    }

    // SignalR wraps negotiate failures, so inspect the status before it is lost.
    class SessionHttpClient extends signalR.DefaultHttpClient {
      async send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
        const url = request.url ? new URL(request.url, window.location.href) : null;
        const isPortalNegotiate = url !== null
          && url.origin === window.location.origin
          && url.pathname === `${SIGNALR_URL}/negotiate`;

        try {
          return await super.send(request);
        } catch (error) {
          if (isPortalNegotiate && error instanceof signalR.HttpError && error.statusCode === 401) {
            stop();
          }
          throw error;
        }
      }
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_URL, {
        httpClient: new SessionHttpClient(signalR.NullLogger.instance),
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("ReceiveNotification", (json: string) => {
      try {
        const n: NotificationItem = JSON.parse(json);
        onNotificationRef.current(n);
      } catch {/* non-critical */}
    });

    connection.on("ReceiveTaskNotification", (json: string) => {
      try {
        const t: TaskReminderItem = JSON.parse(json);
        onTaskRef.current(t);
      } catch {/* non-critical */}
    });

    connection.on("SessionClosed", (reason: string) => {
      stop();
      window.location.href = `/ErrorPages/SessionClosed.aspx?status=${reason}`;
    });

    async function start() {
      if (stopped) {
        return;
      }

      try {
        await connection.start();
      } catch {
        if (!stopped) {
          retryTimer = setTimeout(start, 5000);
        }
      }
    }

    connection.onclose(async () => { await start(); });
    start();

    return stop;
  }, []);
}
